import { FC } from "react";
import { useTranslation } from "react-i18next";
import { MEDIA_OPTIONS, SPEED_THRESOLD_OPTIONS } from "../../constants";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
} from "../../../../components/admin/formFields";

interface SpeedCameraNotificationProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  setFieldTouched: (field: string, isTouched: boolean) => void;
  selectedField: string;
}

const SpeedCameraNotification: FC<SpeedCameraNotificationProps> = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleBlur,
  setFieldTouched,
  selectedField,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.traffic_signal_detection",
  });
  const { t: tMain } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management",
  });

  const eventKey = selectedField?.replace(/Enable$/, "");

  const speed = `${eventKey}_speed`;
  const videoBeforeField = `${eventKey}_videoBefore`;
  const videoAfterField = `${eventKey}_videoAfter`;
  const audioPreWarning = `${eventKey}_audioPreWarning`;
  const audioAlertField = `${eventKey}_audioAlert`;
  const minorMedia = `${eventKey}_minorMedia`;
  const minorGsensor = `${eventKey}_minorGsensor`;
  const moderateMedia = `${eventKey}_moderateMedia`;
  const moderateGsensor = `${eventKey}_moderateGsensor`;
  const severeMedia = `${eventKey}_severeMedia`;
  const severeGsensor = `${eventKey}_severeGsensor`;

  return (
    <>
      <div className="w-full col-span-6 p-6 space-y-1">
        <h1 className="text-lg text-accent-blue-dark font-bold">
          {t("speedCamEnable")}
        </h1>
      </div>
      <div className="col-span-6 px-6 pb-6 border-b grid grid-cols-6">
        <AdminFormFieldDropdown
          label={t("speed_threshold")}
          id={speed}
          name={speed}
          value={String(values[speed])}
          options={SPEED_THRESOLD_OPTIONS}
          onChange={(e) => {
            setFieldValue(speed, e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched(speed, true);
            handleBlur(e);
          }}
          error={errors[speed]}
          touched={touched[speed]}
          customWrapperClass="col-span-3"
        />
      </div>
      <div className="col-span-6 p-6 border-b space-y-1">
        <div className="w-full space-y-2 mb-4">
          <h2 className="block font-semibold text-sm leading-none tracking-normal">
            {t("event_recording")}
          </h2>
          <p className="font-normal text-sm leading-none mb-4 text-gray-500">
            {t("event_recording_description")}
          </p>
        </div>
        <div className="col-span-6 grid grid-cols-12 gap-6">
          <AdminFormFieldInput
            type="number"
            label={t("pre_event_video_duration_sec")}
            name={videoBeforeField}
            id={videoBeforeField}
            value={values[videoBeforeField]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[videoBeforeField]}
            touched={touched[videoBeforeField]}
            customWrapperClass="col-span-6"
          />
          <AdminFormFieldInput
            type="number"
            label={t("post_event_video_duration_sec")}
            name={videoAfterField}
            id={videoAfterField}
            value={values[videoAfterField]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[videoAfterField]}
            touched={touched[videoAfterField]}
            customWrapperClass="col-span-6"
          />
        </div>
      </div>
      <div className="col-span-6 p-6 space-y-1 border-b">
        <h2 className="block font-semibold text-sm leading-none tracking-normal mb-4">
          {tMain("auto_upload")}
        </h2>
        <p className="font-normal text-sm leading-none mb-8 text-gray-500">
          {tMain("choose_upload_data")}
        </p>
        <AdminFormFieldDropdown
          label={tMain("minor")}
          id={minorMedia}
          key={minorMedia}
          name={minorMedia}
          value={values[minorMedia] ?? "snapshot"}
          options={MEDIA_OPTIONS}
          onChange={(e) => {
            setFieldValue(minorMedia, e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched(minorMedia, true);
            handleBlur(e);
          }}
          error={errors[minorMedia]}
          touched={touched[minorMedia]}
          customWrapperClass="col-span-3 mt-6"
        />
        <AdminFormFieldDropdown
          label={tMain("moderate")}
          id={moderateMedia}
          key={moderateMedia}
          name={moderateMedia}
          value={values[moderateMedia] ?? "snapshot"}
          options={MEDIA_OPTIONS}
          onChange={(e) => {
            setFieldValue(moderateMedia, e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched(moderateMedia, true);
            handleBlur(e);
          }}
          error={errors[moderateMedia]}
          touched={touched[moderateMedia]}
          customWrapperClass="col-span-3 mt-6"
        />
        <AdminFormFieldDropdown
          label={tMain("severe")}
          id={severeMedia}
          key={severeMedia}
          name={severeMedia}
          value={values[severeMedia] ?? "snapshot"}
          options={MEDIA_OPTIONS}
          onChange={(e) => {
            setFieldValue(severeMedia, e?.value);
          }}
          onBlur={(e) => {
            setFieldTouched(severeMedia, true);
            handleBlur(e);
          }}
          error={errors[severeMedia]}
          touched={touched[severeMedia]}
          customWrapperClass="col-span-3 mt-6"
        />
      </div>
      <div className="col-span-6 p-6 space-y-1">
        <div className="w-full space-y-2 mb-4">
          <h2 className="block font-semibold text-sm leading-none tracking-normal">
            {t("in_cabin_audio_alert")}
          </h2>
          <p className="font-normal text-sm leading-none mb-4 text-gray-500">
            {t("in_cabin_audio_alert_description")}
          </p>
        </div>
        <div className="col-span-6 grid grid-cols-6 space-x-2">
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-3"
            label={t("approaching_the_traffic_and_signal")}
            id={audioPreWarning}
            type="checkbox"
            name={audioPreWarning}
            checked={values[audioPreWarning]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[audioPreWarning]}
            touched={touched[audioPreWarning]}
          />
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-3"
            label={t("violate_the_traffic_sign_or_signal")}
            id={audioAlertField}
            type="checkbox"
            name={audioAlertField}
            checked={values[audioAlertField]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[audioAlertField]}
            touched={touched[audioAlertField]}
          />
        </div>
      </div>
    </>
  );
};

export default SpeedCameraNotification;
